import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditSplitsModal({ open, onOpenChange, policy, onSuccess }) {
  const [splits, setSplits] = useState([]);
  const [mainAgentPercent, setMainAgentPercent] = useState(0);
  const [deletingSplit, setDeletingSplit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  
  // Add new split
  const [showAddSplit, setShowAddSplit] = useState(false);
  const [currentNPN, setCurrentNPN] = useState('');
  const [currentPercent, setCurrentPercent] = useState('');
  const [verifyingNPN, setVerifyingNPN] = useState(false);
  const [verifiedAgent, setVerifiedAgent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (policy && open) {
      setSplits(policy._split_of_policy || []);
      // Get main agent percent from first split, or calculate from total
      if (policy._split_of_policy && policy._split_of_policy.length > 0) {
        setMainAgentPercent(parseFloat(policy._split_of_policy[0].main_agent_split_percent || 0));
      } else {
        setMainAgentPercent(100);
      }
    }
  }, [policy, open]);

  // Auto-calculate main agent percent whenever splits change
  useEffect(() => {
    const splitAgentsTotal = splits.reduce((sum, s) => sum + parseFloat(s.agent_on_split_percent || 0), 0);
    const calculatedMainPercent = 100 - splitAgentsTotal;
    setMainAgentPercent(calculatedMainPercent);
  }, [splits]);

  const getTotalAllocated = () => {
    return splits.reduce((sum, s) => sum + parseFloat(s.agent_on_split_percent || 0), 0) + mainAgentPercent;
  };

  const getRemainingPercent = () => {
    return 100 - getTotalAllocated();
  };

  const handleVerifyNPN = async () => {
    if (!currentNPN || currentNPN.length < 5) {
      toast.error('Please enter a valid NPN');
      return;
    }

    setVerifyingNPN(true);
    try {
      const response = await axios.post(`${API}/auth/verify-npn`, {
        npn: currentNPN
      });

      if (response.data.status === 'active') {
        const fullName = `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() || 'Agent';
        setVerifiedAgent({
          npn: currentNPN,
          name: fullName,
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          status: 'active'
        });
        toast.success('NPN verified successfully!');
      } else {
        toast.error('This NPN is not active. Please contact support.');
        setVerifiedAgent(null);
      }
    } catch (err) {
      console.error('NPN verification error:', err);
      toast.error('NPN not found or disconnected. Please contact support.');
      setVerifiedAgent(null);
    } finally {
      setVerifyingNPN(false);
    }
  };

  const handleAddNewSplit = () => {
    if (!verifiedAgent) {
      toast.error('Please verify the NPN first');
      return;
    }

    if (!currentPercent || parseFloat(currentPercent) <= 0) {
      toast.error('Please enter a valid percentage');
      return;
    }

    const percent = parseFloat(currentPercent);

    // Add the new split - main agent percentage will auto-adjust
    setSplits([...splits, {
      id: `temp-${Date.now()}`,
      policy_id: policy.id,
      on_split_agent_id: '',
      onsplit_npn: verifiedAgent.npn,
      onsplit_name: verifiedAgent.name,
      agent_on_split_percent: percent,
      isNew: true
    }]);

    // Reset form
    setCurrentNPN('');
    setCurrentPercent('');
    setVerifiedAgent(null);
    setShowAddSplit(false);
    toast.success('Split added! Main agent percentage adjusted automatically.');
  };

  const handleDeleteSplit = async () => {
    if (!deletingSplit) return;

    // If it's a new split (not yet saved), just remove it locally
    if (deletingSplit.isNew || deletingSplit.id.toString().startsWith('temp-')) {
      setSplits(splits.filter(s => s.id !== deletingSplit.id));
      toast.success('Split removed!');
      setShowDeleteConfirm(false);
      setDeletingSplit(null);
      return;
    }

    // If it's an existing split, call the API to delete it
    try {
      await axios.post(`${API}/portal/split/delete`, {
        split_id: deletingSplit.id
      });
      
      setSplits(splits.filter(s => s.id !== deletingSplit.id));
      toast.success('Split deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingSplit(null);
    } catch (err) {
      console.error('Error deleting split:', err);
      toast.error('Failed to delete split. Please try again.');
    }
  };

  const handleUpdatePercent = (splitId, newPercent) => {
    const updated = splits.map(s => 
      s.id === splitId ? { ...s, agent_on_split_percent: parseFloat(newPercent) || 0 } : s
    );
    setSplits(updated);
  };

  const handleSubmit = () => {
    // Show confirmation modal with summary
    setShowSaveConfirm(true);
  };

  const handleConfirmSave = async () => {
    setSubmitting(true);
    try {
      const payload = {
        policy_id: policy.id,
        splits: splits.map(s => ({
          split_id: s.isNew || s.id.toString().startsWith('temp-') ? null : s.id,
          main_agent_split_percent: mainAgentPercent,
          agent_onsplit_npn: s.onsplit_npn,
          agent_on_split_percent: s.agent_on_split_percent
        }))
      };

      await axios.post(`${API}/portal/split/upsert`, payload);
      toast.success('Splits updated successfully!');
      setShowSaveConfirm(false);
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error updating splits:', err);
      toast.error('Failed to update splits. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Edit Policy Splits</DialogTitle>
            <DialogDescription>
              Manage split allocation for policy #{policy?.policy_number}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Main Agent */}
            <div className="bg-[#f4e5b8] p-4 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-700">Main Agent (Owner)</p>
                  <p className="text-xs text-neutral-500">{policy?.agentOwner_name || policy?.policyAgent_owner}</p>
                  <p className="text-xs text-neutral-400 mt-1">Auto-calculated based on split agents</p>
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    value={mainAgentPercent}
                    readOnly
                    disabled
                    className="text-center rounded-lg font-semibold bg-neutral-100"
                  />
                </div>
                <span className="text-sm font-medium text-neutral-700">%</span>
              </div>
            </div>

            {/* Existing Splits */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Split Agents ({splits.length})</Label>
              {splits.map((split) => (
                <div key={split.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-800">{split.onsplit_name}</p>
                    <p className="text-xs text-neutral-500">NPN: {split.onsplit_npn}</p>
                  </div>
                  <div className="w-24">
                    <Input
                      type="number"
                      value={split.agent_on_split_percent}
                      onChange={(e) => handleUpdatePercent(split.id, e.target.value)}
                      min="0"
                      max="100"
                      className="text-center rounded-lg"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeletingSplit(split);
                      setShowDeleteConfirm(true);
                    }}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Remaining Allocation Info */}
            {getRemainingPercent() !== 0 && (
              <div className={`p-3 rounded-xl ${getRemainingPercent() > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`text-sm ${getRemainingPercent() > 0 ? 'text-yellow-800' : 'text-red-800'}`}>
                  {getRemainingPercent() > 0 ? (
                    <>
                      <strong>{getRemainingPercent()}%</strong> remaining - You can add more splits or adjust percentages
                    </>
                  ) : (
                    <>
                      <strong>Over by {Math.abs(getRemainingPercent())}%</strong> - Please adjust percentages to total 100%
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Add New Split */}
            {!showAddSplit && (
              <Button
                onClick={() => setShowAddSplit(true)}
                variant="outline"
                className="w-full border-[#d4af37] text-[#b8941e] hover:bg-[#f4e5b8] rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Split
              </Button>
            )}

            {showAddSplit && (
              <div className="space-y-4 p-4 border-2 border-dashed border-neutral-300 rounded-xl">
                <Label className="text-sm font-medium">Add New Split Agent</Label>
                
                <div className="space-y-2">
                  <Label className="text-xs text-neutral-600">Agent NPN *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter NPN"
                      value={currentNPN}
                      onChange={(e) => {
                        setCurrentNPN(e.target.value);
                        setVerifiedAgent(null);
                      }}
                      className="rounded-xl"
                    />
                    <Button
                      onClick={handleVerifyNPN}
                      disabled={verifyingNPN || !currentNPN}
                      className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
                    >
                      {verifyingNPN ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                  {verifiedAgent && (
                    <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Verified: <strong>{verifiedAgent.name}</strong> (NPN: {verifiedAgent.npn})
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-neutral-600">Split Percentage *</Label>
                  <Input
                    type="number"
                    placeholder="Enter percentage"
                    value={currentPercent}
                    onChange={(e) => setCurrentPercent(e.target.value)}
                    min="0"
                    max="100"
                    className="rounded-xl"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setShowAddSplit(false);
                      setCurrentNPN('');
                      setCurrentPercent('');
                      setVerifiedAgent(null);
                    }}
                    variant="outline"
                    className="flex-1 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddNewSplit}
                    disabled={!verifiedAgent || !currentPercent}
                    className="flex-1 bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
                  >
                    Add Split
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Split?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deletingSplit?.onsplit_name}</strong> from this policy split?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSplit}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              Delete Split
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Confirmation Modal */}
      <Dialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Confirm Split Changes</DialogTitle>
            <DialogDescription>
              Please review the final allocation before saving
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Main Agent Summary */}
            <div className="bg-[#f4e5b8] p-4 rounded-xl border-2 border-[#d4af37]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-neutral-800">Main Agent (Owner)</p>
                  <p className="text-xs text-neutral-600">{policy?.agentOwner_name || policy?.policyAgent_owner}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#d4af37]">{mainAgentPercent.toFixed(2)}%</p>
                </div>
              </div>
            </div>

            {/* Split Agents Summary */}
            {splits.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-neutral-700">Split Agents ({splits.length})</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {splits.map((split, index) => (
                    <div key={split.id} className="bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-neutral-800">{split.onsplit_name}</p>
                          <p className="text-xs text-neutral-500">NPN: {split.onsplit_npn}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-neutral-800">{split.agent_on_split_percent}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total Allocation */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-neutral-700">Total Allocation</p>
                <p className={`text-xl font-bold ${getTotalAllocated() === 100 ? 'text-green-600' : getTotalAllocated() < 100 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {getTotalAllocated()}%
                </p>
              </div>
              {getTotalAllocated() !== 100 && (
                <p className="text-xs text-yellow-600 mt-1">
                  ⚠️ Warning: Total allocation is {getTotalAllocated() < 100 ? 'under' : 'over'} 100%
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSaveConfirm(false)}
              disabled={submitting}
              className="rounded-xl"
            >
              Go Back
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={submitting}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm & Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}